import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import {Observable, of, Subject} from "rxjs";
import { AppService } from "@irb/app.service";
import { UtilityService } from "@irb-common/services/utility.service";
import { IPortfolioDetail, IAccountDetail, IModelDetailRequest, IModelDetailsResponse,IModelDetail } from "@irb/irb-editors/irb-editors.model";
import { delay, shareReplay } from "rxjs/operators";
import { map } from "rxjs/operators";
import { IMultipleAccountEditRequest } from '@irb/irb-clients/irb-clients.model';
import {IUserDetails} from "@irb/app.model";


@Injectable({
  providedIn: "root"
})
export class IrbEditorsService {
  httpOptions = {
    headers: new HttpHeaders({
      "Content-Type": "application/json",
      "Cache-Control": "no-cache",
      "Pragma": "no-cache",
      "Expires": "Sat, 01 Jan 2000 00:00:00 GMT"
    })
  };

  _expandedPanels: Array<string> = [];
  _defaultExpandedPanels: Array<string> = [];
  maxPanels: number;
  panelName: string;
  securitiesForAllEqvSubject = new Subject<any>();
  isUseSecurityService = false;
  getSecuritiesObservable$: Observable<any>;
  getCustomCategoriesObservable$: Observable<any>;
  securityServiceUrlPrefix = '/irebalone/security';

  constructor(private _http: HttpClient, private as: AppService, private util: UtilityService) {
      this.getUserDetails();
  }

  getUserDetails(): void {
    this.as.getUserDetails().subscribe((userDetails: IUserDetails) => {
      if (userDetails) {
        this.isUseSecurityService = userDetails.featureFlags && userDetails.featureFlags.ENABLE_SECURITY_APIS;
        if (this.isUseSecurityService)
          this.securityServiceUrlPrefix = '/irebal-security';
      }
    });
  }

  /************************/
  //Portflio Endpoints
  getPortfolioDetails(portfolioId: string): Observable<Object> {
    return this._http.get("/irebalone/portfolios/get-portfolio/" + portfolioId, this.httpOptions);
  }

  savePortfolioDetails(portfolioDetail: IPortfolioDetail) {
    return this._http.post("/irebalone/portfolios/edit-single-portfolio-async", JSON.stringify(portfolioDetail), this.httpOptions);
  }
  savePortfolioDetailsStatus(statusID: any) {
    return this._http.get("/irebalone/portfolios/get-portfolio-status/" + statusID, this.httpOptions);
  }
  getModelDetails(): Observable<Object> {
    return this._http.get("/irebalone/portfolios/get-model-details", this.httpOptions);
  }

  getCategories(): Observable<Object> {
    return this._http.get("/irebalone/portfolios/get-categories", this.httpOptions);
  }

  getCustomCategories(): Observable<Object> {
    if (!this.getCustomCategoriesObservable$) {
      let url = `${this.securityServiceUrlPrefix}/get-custom-categories`;
      this.getCustomCategoriesObservable$ = this._http.get(url, this.httpOptions).pipe(
        map(resp => this.defineResponse(resp)),
        shareReplay(1)
      );
    }
    return this.getCustomCategoriesObservable$;
  }

  getSecurities(): Observable<Object> {
    if (!this.getSecuritiesObservable$) {
      this.getSecuritiesObservable$ = this._http.get("/irebalone/portfolios/show-securities", this.httpOptions).pipe(
          shareReplay(1)
      );
    }
    return this.getSecuritiesObservable$;
  }

  /************************/
  //Account Endpoints

  getAccountDetails(accountId: string): Observable<Object> {
    return this._http.get("/irebalone/accounts/fetch-account-details/" + accountId, this.httpOptions);
  }

  getSetAsideCash(accountId: string): Observable<Object> {
    return this._http.get("/irebalone/accounts/fetch-set-aside-cash/" + accountId, this.httpOptions);
  }

  saveAccountDetails(accountDetail: IAccountDetail) {
    return this._http.post("/irebalone/accounts/edit-account", JSON.stringify(accountDetail), this.httpOptions);
  }

  getPortDropDown(s: string): Observable<Object> {
    return this._http.get("/irebalone/accounts/port-drop-down/" + s, this.httpOptions);
  }

  getSecuritiesSearchDropDown(s: string): Observable<Object> {
    const params = new HttpParams().set('searchString', s);
    return this._http.get("/irebalone/model/get-securities-search-drop-down", { ...this.httpOptions, params });
  }

  getSecuritieAllocations(selectedIds: number[]){
    return this._http.post("/irebalone/model/get-securities-info", JSON.stringify(selectedIds), this.httpOptions);
  }

  getModelAssociationDetails(selectedModel: IModelDetailRequest): Observable<Object> {
    if(selectedModel.modelId == -1) {
      return of({
          "hasValidBlendedRelation": false,
          "modelAssociated": false,
          "hasValidPortfolioRelation": false
      });
    }

    return this._http.post("/irebalone/model/get-model-association-details", JSON.stringify(selectedModel), this.httpOptions);
  }

  getAccountDropDown(accountId: string): Observable<Object> { //Get funding account numbers for a SMA
    return this._http.get("/irebalone/accounts/account-drop-down/" + accountId, this.httpOptions);
  }

  getSecuritiesDropDown(): Observable<Object> { //Get SMA symbols for a SMA
    return this._http.get("/irebalone/accounts/securety-master-drop-down", this.httpOptions);
  }

  validatePortfolioName(portfolioName:any) {
    const options = { headers: this.httpOptions.headers};
    const url = `/irebalone/portfolios/portfolio-exists-active`;
    return this._http.post(url, {portfolioName:portfolioName}, options)
  }
  getSelectedAccountsDetails(data: any) {
    return this._http.post("/irebalone/accounts/get-selected-accounts-details" , JSON.stringify(data), this.httpOptions);
  }

  saveMultipleAccounts(multipleAccountEditRequest: IMultipleAccountEditRequest) {
    return this._http.post("/irebalone/accounts/edit-multiple-accounts" , JSON.stringify(multipleAccountEditRequest), this.httpOptions);
  }
  
  saveMultipleAccountsAsync(multipleAccountEditRequest: IMultipleAccountEditRequest) {
    return this._http.post("/irebalone/accounts/save-multiple-accounts-async" , JSON.stringify(multipleAccountEditRequest), this.httpOptions);
  }

  checkSaveMultipleAccountsStatus(rprasId: string) {
    return this._http.get("/irebalone/accounts/check-multiple-acct-status/" + rprasId, this.httpOptions);
  }

  getModelDetailsForEditModel(modelDetailsReq: IModelDetailRequest) {
    return this._http.post("/irebalone/model/get-model-details", JSON.stringify(modelDetailsReq), this.httpOptions);
  }

  saveModelDetails(modelDetail: IModelDetailsResponse) {
    return this._http.post("/irebalone/model/edit-security-model-alloc", JSON.stringify(modelDetail), this.httpOptions);
  }

  validateSecurityModelName(securityModelName: any, type: string) {
    const modelExistsDTO = {"modelName": securityModelName, "type": type};
    return this._http.post("/irebalone/model/is-model-exists", JSON.stringify(modelExistsDTO), this.httpOptions);
  }
  
  getSecurityModelHistory(modelId: any, type: string): Observable<Object> { //Get SMA symbols for a SMA
    if(modelId == -1) {
      return of([]);
    }

    return this._http.get("/irebalone/model/view-model-history/"+ modelId+"/"+type, this.httpOptions);
  }

  getAutoToleranceValues(secModelTarget: any) {
    return this._http.post("/irebalone/model/get-auto-tolerance-values", JSON.stringify(secModelTarget), this.httpOptions);
  }

  getModelFinscore(modelObj: any) {
    return this._http.post("/irebalone/model/finscore", JSON.stringify(modelObj), this.httpOptions);
  }

  saveBlendedModel(blendedModel: any) {
    return this._http.post("/irebalone/model/edit-blended-security-model", JSON.stringify(blendedModel), this.httpOptions);
  }

  getSecurityModelDropDownForBlended() {
    return this._http.get("/irebalone/model/get-security-model-drop-down-for-blended", this.httpOptions);
  }

  getBlendedModelHistory(modelId: string): Observable<Object> { //Get SMA symbols for a SMA
    return this._http.get("/irebalone/model/view-blend-model-history/" + modelId+"/view", this.httpOptions);
  }

  getAccoutExternalHoldings(accountId:any) {
    return this._http.get("/irebalone/accounts/get-ext-acct-hlds/"+accountId, this.httpOptions);
  }

  getExtSecurityHoldings() {
    return this._http.get("/irebalone/accounts/get-sec-drop-down" ,this.httpOptions);
  }

  /*******************/

  /*******Included Portfolios--Rebalance--Start */
  getCustomCategoriesRebal() {
    return this._http.get("/irebalone/rebalance-portfolios/categories-drop-down", this.httpOptions);
  }

  getPortFoliosDropDown() {
    return this._http.get("/irebalone/rebalance-portfolios/portfolios-drop-down", this.httpOptions);
  }

  getModelsRebal() {
    return this._http.get("/irebalone/rebalance-portfolios/models-drop-down", this.httpOptions);
  }

  getPortfoliosRebal(_portfolioidlist:any) {
    return this._http.post("/irebalone/rebalance-portfolios/get-portfolios",JSON.stringify(_portfolioidlist), this.httpOptions);
  }

  getPortfoliosRebalStatus(_statusId:any) {
    return this._http.get("/irebalone/rebalance-portfolios/async-status-blob/" + _statusId, this.httpOptions);
  }

  importExtraCashRebal(formDta:any,analysisId:number) {
    return this._http.post("/irebalone/rebalance-portfolios/import-cash-settings/"+analysisId,formDta,{responseType: 'text'});
  }

  getImportTaxLossSettings(formDta:any,analysisId:number) {
    return this._http.post("/irebalone/rebalance-portfolios/import-loss-target-settings/"+analysisId,formDta,{responseType: 'text'});
  }
  importValidLossTargetSettings(validRecords: any,analysisId:number) {
    return this._http.post("/irebalone/rebalance-portfolios/import-valid-loss-target-settings/"+analysisId,validRecords,{responseType: 'text'});
  }
  getAccountDetailsRebal(portfolioId:any) {
    return this._http.post("/irebalone/rebalance-portfolios/get-account-details",JSON.stringify(portfolioId), this.httpOptions);
  
  }
  getSmartFilterDetails(data:any) {
    return this._http.post("/irebalone/rebalance-portfolios/get-smart-filtered-portfolios",JSON.stringify(data), this.httpOptions);
  }
   /*******Included Portfolios--Rebalance--End */
  /************************/
  /*******Securities End Points --- Start */
  getSecurityDetail(securityId: string) {
    return this._http.get("/irebalone/security/get-security-detail?securityId="+securityId,this.httpOptions);
  }

  getMultipleSecurityDetail(data: any []) {
    return this._http.post("/irebalone/security/get-multiple-security-details", JSON.stringify(data),this.httpOptions);
  }

  getMinSubAlternatives() {
    let url = `${this.securityServiceUrlPrefix}/get-min-sub`;
    return this._http.get(url, this.httpOptions).pipe(map(res => this.defineResponse(res)));
  }

  getGroupComponentSecurities(securityId: string) {
    return this._http.get("/irebalone/security/get-group-component-securities/"+securityId,this.httpOptions);
  }
  getAllMmfSecuritiesForCashEqv() {
    let url = `${this.securityServiceUrlPrefix}/get-all-mmf-securities-for-cash-eqv`;
    url = "/irebalone/security/get-all-mmf-securities-for-cash-eqv"; //temporary. TODO: remove when resolved on security service
    return this._http.get(url, this.httpOptions).pipe(map(res => this.defineResponse(res)));
  }
  getAllSecuritiesForAllEqv(securityId: string) {
    return this._http.get("/irebalone/security/get-all-securities-for-all-eqv/" + securityId, this.httpOptions);
  }

  getAllSecuritiesForAllEqvs(data: any[]) {
      return this._http.post("/irebalone/security/get-all-securities-for-all-eqvs/", JSON.stringify(data), this.httpOptions);
  }
  getAllocationEquivalentSecs(securityId: string) {
    const e = encodeURIComponent(securityId);
    let url = `${this.securityServiceUrlPrefix}/get-allocation-equivalent-secs/${e}`;
    return this._http.get(url, this.httpOptions).pipe(map(res => this.defineResponse(res)));
  }

  getInvrsSub() {
    return this._http.get("/irebalone/security/get-invrs-sub", this.httpOptions);
  }
  getAllSecuritiesForGroupComponent(securityId: string) {
    const e = encodeURIComponent(securityId);
    let url = `${this.securityServiceUrlPrefix}/get-all-sec-for-grp-component/${e}`;
    return this._http.get(url,this.httpOptions).pipe(map(res => this.defineResponse(res)));
  }



  saveSecurityProfile(data: any) {
    return this._http.post("/irebalone/security/save-security-profile", JSON.stringify(data), this.httpOptions);
  }
  saveMultipleSecurityProfile(data: any[]) {
    return this._http.post("/irebalone/security/save-multiple-security-profiles", JSON.stringify(data), this.httpOptions);
  }

  saveMultipleSecurityProfilesAsync(data: any[]) {
    return this._http.post("/irebalone/security/save-multiple-security-profiles-async", JSON.stringify(data), this.httpOptions);
  }

  actionStatusCheck(actionStatusId: any) {
    return this._http.get('/irebalone/security/get-async-status-for-securities/' + actionStatusId).pipe(delay(1000));
    // false && console.log(actionStatusId);
    // return of({
    //     "responseStatus": "SUCCESS",
    //     "responseData": {
    //         "statusId": 61,
    //         "action": "PF_DELETE",
    //         "moduleName": "PF_MODULE",
    //         "reqStatus": "IN_PROGRESS",
    //         "envId": 74830353,
    //         "recievedCount": 2,
    //         "successRecCount": 1,
    //         "failedRecCount": 0,
    //         "failMsg": "StaleDataException",
    //         "requestedBy": "QA_Test123",
    //         "requestedDate": 1688668614706,
    //         "lastModifiedBy": "QA_Test123",
    //         "lastModifiedDate": 1688668615817
    //     },
    //     "responseUtilityMap": null
    // });
  }


  /*******Securities End Points --- End */
        /****Harvest Tax Loss calls */
  getTaxLossHarvestDetail() {
    return this._http.get('/irebalone/rebalance-portfolios/recent-htl-drop-down', this.httpOptions);
  }

  getTaxLossHarvestClone(frmData: any) {
    return this._http.post('/irebalone/rebalance-portfolios/clone-htl', JSON.stringify(frmData), this.httpOptions);
  }

  getTaxLossSecuritiesDropDown() {
    return this._http.get('/irebalone/rebalance-portfolios/securities-drop-down/ALL', this.httpOptions);
  }
       
        /****Harvest Tax Loss calls */

  updateExpansionState(itemName: string, newState: string) {
    const index = this.expandedPanels.indexOf(itemName);

    if (index === -1 && newState === "opened") {
      this.expandedPanels.push(itemName);
    } else if (newState === "closed") {
      this.expandedPanels.splice(index, 1);
    }

    this.saveExpansionState();
  }


  getDefaultExpansionState(itemName: string) {
    return (this.defaultExpandedPanels.indexOf(itemName) !== -1);
  }

  loadExpansionState(initMaxPanels: number, initPanelName: string, defaultExpandedPanels?: Array<string>, sessionOverridesDefault?: boolean) {
    if(typeof sessionOverridesDefault === "undefined") {
      sessionOverridesDefault = true;
    }

    this.maxPanels = initMaxPanels;
    this.panelName = initPanelName;
    const dep = (defaultExpandedPanels) ? defaultExpandedPanels : [];

    if(sessionOverridesDefault) {
      let s = this.as.getFromSession(this.panelName);

      if(s) {
        s = s.filter((x: any) => dep.indexOf(x) !== -1);
      }
           
      this.defaultExpandedPanels = s || dep;
    } else {
      this.defaultExpandedPanels = dep;
    }
    
    this.expandedPanels = this.util.clone(this.defaultExpandedPanels);
  }

  updateExpansionStateOpenTriggers(itemName: string, newState: string) {
    const index = this.expandedPanels.indexOf(itemName);
    
    if (index === -1 && newState === "opened") {
      this.expandedPanels.push(itemName);
    } else if (newState === "closed") {
      this.expandedPanels.splice(index, 1);
    }
  }

  saveExpansionState() {
    this.as.setToSession(this.panelName, this.expandedPanels);
  }

  get expandedPanels(): Array<string> {
    return this._expandedPanels;
  }

  set expandedPanels(a: Array<string>) {
    this._expandedPanels = a;
  }

  get defaultExpandedPanels(): Array<string> {
    return this._defaultExpandedPanels;
  }

  set defaultExpandedPanels(a: Array<string>) {
    this._defaultExpandedPanels = a;
  }

  finMansonTooltip() {
    const msg = `
            <div class="block">The FinScore is a third-party score provided by FinMason. This score attempts to measure the risk of a portfolio on a scale of 1 to 100, with 1 being the safest and 100 being the riskiest. Many people find the 1-100 scale more intuitive than some of the statistical outputs used by investment professionals. Specific points on this scale are roughly in line with how professional asset allocators classify&nbsp;risk:</div>

            <ul class="block">
              <li>FinScore 10: Conservative</li>
              <li>FinScore 30: Moderately Conservative</li>
              <li>FinScore 50: Moderate</li>
              <li>FinScore 70: Moderately Aggressive</li>
              <li>FinScore 90: Aggressive</li>
            </ul>

            <div class="block"><strong>Limitations:</strong></div>

            <ol class="block">
              <li>FinScore is calculated using the targeted security and does not factor in equivalency rules.</li>
              <li>If the FinScore value is empty, that indicates the model is targeting an unrecognized security, e.g. custom, group, SMA, etc.</li>
            </ol>

            <div>Schwab and FinMason are not affiliated.</div>
        `;

    return msg;
  }

  saveModelDetailsAsync(modelDetail: IModelDetailsResponse) {
    return this._http.post("/irebalone/model/async/edit-security-model-alloc", JSON.stringify(modelDetail), this.httpOptions);
  }

  actionStatusCheckModel(statusId: any) {
    return this._http.get("/irebalone/model/get-async-response/" + statusId, this.httpOptions);
  }

  getOnlyPortfolioDetails(portfolioId: string): Observable<Object> {
    return this._http.get("/irebalone/portfolios/get-portfolio-details/" + portfolioId, this.httpOptions);
  }

  getLabels(): Observable<Object> {
    return this._http.get("/irebalone/portfolios/get-labels");

    /*let d: any = [];
    let a: any = ["X","Y","Z"];

    for(let i = 0; i < 5; i++) {
      let c = Math.floor(Math.random() * 3);

      d.push({
        name: "Label " + i + " " + a[c],
        description: "Description for Label " + i + " " + a[c],
        id: i
      });
    }

    return of(d);*/
  }

  getPortfolioAssignedAccounts(portfolioId: string): Observable<Object> {
    return this._http.get("/irebalone/portfolios/get-portfolio-assigned-accounts/" + portfolioId, this.httpOptions);
  }
  getPortfolioRestrictions(portfolioId: string): Observable<Object> {
    return this._http.get("/irebalone/portfolios/get-portfolio-restrictions/" + portfolioId, this.httpOptions);
  }
  getPortfolioScheduledRebalances(portfolioId: string): Observable<Object> {
    return this._http.get("/irebalone/portfolios/get-portfolio-scheduled-rebalances/" + portfolioId, this.httpOptions);
  }
  getPortfolioNotes(portfolioId: string): Observable<Object> {
    return this._http.get("/irebalone/portfolios/get-portfolio-notes/" + portfolioId, this.httpOptions);
  }

  getPortfolioHistory(params: any, portfolioId: any) {
    const headers = new HttpHeaders({
      'Filter-Item':'items=' + params.request.startRow + '-' + (--params.request.endRow)
    });

    let sort = 'sort(-createdDate)';
    if (params.request.sortModel && params.request.sortModel.length > 0) {
      let sortColumn = params.request.sortModel[0].colId;
      const sortOrder = (params.request.sortModel[0].sort === 'asc') ? '+' : '-';
      sort = 'sort(' + sortOrder + sortColumn + ')';
    }
    return this._http.get("/irebalone/portfolios/portfolio-history/" + portfolioId + "?" + sort, {headers: headers});
  }

  exportPortfolioHistory(portfolioId: any) {
    const payload = [portfolioId];
    return this._http.post("/irebalone/portfolios/async/export-portfolio-history-data",JSON.stringify(payload), this.httpOptions);
  }

  getAccountHistory(params: any, accountId: any) {
    const headers = new HttpHeaders({
      'Filter-Item':'items=' + params.request.startRow + '-' + (--params.request.endRow)
    });

    let sort = 'sort(-createdDate)';
    if (params.request.sortModel && params.request.sortModel.length > 0) {
      let sortColumn = params.request.sortModel[0].colId;
      const sortOrder = (params.request.sortModel[0].sort === 'asc') ? '+' : '-';
      sort = 'sort(' + sortOrder + sortColumn + ')';
    }
    return this._http.get("/irebalone/accounts/account-history/" + accountId + "?" + sort, {headers: headers});
  }

  exportAccountHistory(accountId: any) {
    const payload = [accountId];
    return this._http.post("/irebalone/accounts/async/export-account-history-data", JSON.stringify(payload), this.httpOptions);
  }

  getPortfolioAutoScheduler(portfolioId: string): Observable<Object> {
    return this._http.get("/irebalone/portfolios/get-portfolio-auto-scheduler/" + portfolioId, this.httpOptions);
  }

  getPfAutoRebalResultList(params: any, portfolioId: any) {
    const headers = new HttpHeaders({
      'Filter-Item':'items=' + params.request.startRow + '-' + (--params.request.endRow)
    });

    let sort = 'sort(-createdOn)';
    if (params.request.sortModel && params.request.sortModel.length > 0) {
      let sortColumn = params.request.sortModel[0].colId;
      const sortOrder = (params.request.sortModel[0].sort === 'asc') ? '+' : '-';
      sort = 'sort(' + sortOrder + sortColumn + ')';
    }
    return this._http.get("/irebalone/portfolios/get-pf-auto-rebal-result-list/" + portfolioId + "?" + sort, {headers: headers});
  }

  getAutoSchdBlockedPortfolio(multiplePortfolioEditRequest: IMultipleAccountEditRequest) {
    return this._http.post("/irebalone/portfolios/get-auto-schd-blocked-portfolio", JSON.stringify(multiplePortfolioEditRequest), this.httpOptions);
  }

  passSecuritiesForAllEqvSubject(securitiesForAllEqvSubject: any) {
    this.securitiesForAllEqvSubject.next(securitiesForAllEqvSubject);
  }

  getPortfolioEquivalents(portfolioId: string): Observable<Object> {
    return this._http.get("/irebalone/portfolios/get-portfolio-equivalents/" + portfolioId, this.httpOptions);
  }

    defineResponse(res: any) {
        return  res && res.data && res.data.irebalResponse ? res.data.irebalResponse : res;
    }


  getSecurityHistory(params: any, securityId: any) {
    const headers = new HttpHeaders({
      'Filter-Item':'items=' + params.request.startRow + '-' + (--params.request.endRow)
    });

    let sort = 'sort(-createdDate)';
    if (params.request.sortModel && params.request.sortModel.length > 0) {
      let sortColumn = params.request.sortModel[0].colId;
      const sortOrder = (params.request.sortModel[0].sort === 'asc') ? '+' : '-';
      sort = 'sort(' + sortOrder + sortColumn + ')';
    }
    const url =  '/irebal-security/security-history/' + securityId + '?' + sort;
    return this._http.get(url , { headers: headers }).pipe(map(resp => this.defineResponse(resp)));
  }

  exportSecurityHistory(securityId: any) {
    const url = '/irebal-security/async/export-security-history-data';
    return this._http.post(url, JSON.stringify(securityId), this.httpOptions).pipe(map(resp => this.defineResponse(resp)));
  }
}
